import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { Link } from 'react-router-dom'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalMarcas: 0,
    eventosActivos: 0,
    comentariosPendientes: 0,
    inscripcionesMes: 0,
    contactosSinLeer: 0,
    reclamosPendientes: 0
  })
  const [loading, setLoading] = useState(true)
  const [recentActivity, setRecentActivity] = useState([])

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)

      // Total de marcas
      const { count: marcasCount } = await supabase
        .from('tiendas')
        .select('*', { count: 'exact', head: true })

      // Eventos activos
      const { count: eventosCount } = await supabase
        .from('eventos')
        .select('*', { count: 'exact', head: true })
        .eq('estado', 'Activo')

      // Comentarios pendientes de aprobación
      const { count: comentariosCount } = await supabase
        .from('comentarios')
        .select('*', { count: 'exact', head: true })
        .eq('publicado', false)

      // Inscripciones del mes actual
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString()
      const { count: inscripcionesCount } = await supabase
        .from('inscripciones_eventos')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', firstDayOfMonth)

      // Contactos sin leer
      const { count: contactosCount } = await supabase
        .from('contacto_marcas')
        .select('*', { count: 'exact', head: true })
        .eq('leido', false)

      // Reclamos pendientes
      const { count: reclamosCount } = await supabase
        .from('libro_reclamaciones')
        .select('*', { count: 'exact', head: true })
        .eq('estado', 'Pendiente')

      // Actividad reciente (últimos comentarios)
      const { data: comentariosRecientes } = await supabase
        .from('comentarios')
        .select(`
          *,
          tiendas(nombre)
        `)
        .eq('publicado', false)
        .order('created_at', { ascending: false })
        .limit(5)

      setStats({
        totalMarcas: marcasCount || 0,
        eventosActivos: eventosCount || 0,
        comentariosPendientes: comentariosCount || 0,
        inscripcionesMes: inscripcionesCount || 0,
        contactosSinLeer: contactosCount || 0,
        reclamosPendientes: reclamosCount || 0
      })

      setRecentActivity(comentariosRecientes || [])
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, icon, color, link }) => (
    <Link 
      to={link} 
      style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'none',
        border: 'var(--card-border)',
        padding: '1.5rem',
        textDecoration: 'none',
        display: 'block',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)'
        e.currentTarget.style.boxShadow = 'none'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <h3 style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--small)',
            color: 'var(--textMuted)',
            margin: '0 0 0.5rem 0',
            fontWeight: 'var(--font-weight-medium)'
          }}>
            {title}
          </h3>
          <p style={{
            fontFamily: 'var(--font-display)',
            fontSize: '2rem',
            fontWeight: 'var(--font-weight-regular)',
            color: color,
            margin: 0
          }}>
            {loading ? '...' : value}
          </p>
        </div>
        <div style={{ fontSize: '2.5rem' }}>{icon}</div>
      </div>
    </Link>
  )

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem'
      }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--displayXL)',
          fontWeight: 'var(--font-weight-regular)',
          color: 'var(--textPrimary)',
          margin: 0
        }}>
          DASHBOARD
        </h1>
        <button
          onClick={fetchDashboardData}
          style={{
            padding: '0.75rem 1.25rem',
            background: 'var(--ctaBg)',
            color: 'var(--ctaText)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--body)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
            boxShadow: 'none',
            border: 'var(--card-border)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)'
            e.currentTarget.style.boxShadow = 'none'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = 'none'
          }}
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <StatCard
          title="Total Marcas"
          value={stats.totalMarcas}
          icon="🏪"
          color="var(--accentPurpleDark)"
          link="/admin/marcas"
        />
        <StatCard
          title="Eventos Activos"
          value={stats.eventosActivos}
          icon="🎉"
          color="#10B981"
          link="/admin/eventos"
        />
        <StatCard
          title="Comentarios Pendientes"
          value={stats.comentariosPendientes}
          icon="💬"
          color="#F97316"
          link="/admin/comentarios"
        />
        <StatCard
          title="Inscripciones del Mes"
          value={stats.inscripcionesMes}
          icon="📝"
          color="#3B82F6"
          link="/admin/eventos"
        />
        <StatCard
          title="Contactos Sin Leer"
          value={stats.contactosSinLeer}
          icon="✉️"
          color="#8B5CF6"
          link="/admin/contactos"
        />
        <StatCard
          title="Reclamos Pendientes"
          value={stats.reclamosPendientes}
          icon="📋"
          color="#EF4444"
          link="/admin/reclamos"
        />
      </div>

      {/* Recent Activity */}
      <div style={{
        background: 'var(--surface)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'none',
        border: 'var(--card-border)',
        padding: '1.5rem'
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--h2)',
          fontWeight: 'var(--font-weight-regular)',
          color: 'var(--textPrimary)',
          margin: '0 0 1rem 0'
        }}>
          COMENTARIOS PENDIENTES DE APROBACIÓN
        </h2>
        
        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            fontFamily: 'var(--font-ui)',
            color: 'var(--textMuted)'
          }}>
            <LoadingSpinner message="" size={48} />
          </div>
        ) : recentActivity.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            fontFamily: 'var(--font-ui)',
            color: 'var(--textMuted)'
          }}>
            No hay comentarios pendientes de aprobación
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}>
            {recentActivity.map((comentario) => (
              <div
                key={comentario.id}
                style={{
                  borderLeft: '4px solid #F97316',
                  paddingLeft: '1rem',
                  paddingTop: '0.5rem',
                  paddingBottom: '0.5rem',
                  borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--background)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'start',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.25rem',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontWeight: 'var(--bold)',
                        fontSize: 'var(--body)',
                        color: 'var(--textPrimary)'
                      }}>
                        {comentario.usuario_nombre}
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--small)',
                        color: 'var(--textMuted)'
                      }}>
                        en
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--small)',
                        fontWeight: 'var(--medium)',
                        color: 'var(--accentPurpleDark)'
                      }}>
                        {comentario.tiendas?.nombre || 'Marca desconocida'}
                      </span>
                    </div>
                    <p style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--body)',
                      color: 'var(--textPrimary)',
                      margin: '0 0 0.5rem 0'
                    }}>
                      {comentario.comentario}
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--small)',
                        color: 'var(--textMuted)'
                      }}>
                        {new Date(comentario.created_at).toLocaleDateString('es-PE', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <span style={{ color: '#FF6B35' }}>{'⭐'.repeat(comentario.puntuacion)}</span>
                    </div>
                  </div>
                  <Link
                    to="/admin/comentarios"
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--small)',
                      color: 'var(--accentPurpleDark)',
                      textDecoration: 'none',
                      fontWeight: 'var(--medium)',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Revisar →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {recentActivity.length > 0 && (
          <div style={{
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--mutedStroke)',
            textAlign: 'center'
          }}>
            <Link
              to="/admin/comentarios"
              style={{
                fontFamily: 'var(--font-ui)',
                color: 'var(--accentPurpleDark)',
                textDecoration: 'none',
                fontWeight: 'var(--font-weight-medium)',
              }}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              VER TODOS LOS COMENTARIOS →
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}




